import { applyQuat, quatFromAxisAngle, quatMultiply, type Quat } from "./math";

const UP = { x: 0, y: 1, z: 0 };
const FORWARD = { x: 0, y: 0, z: -1 };

/**
 * Re-references a 3DoF orientation stream to the user's INITIAL gaze, so the sign is placed dead
 * ahead at start instead of at an absolute compass heading. Without this the object lands at an
 * arbitrary world heading on iOS (raw `deviceorientation` alpha has no stable zero), so the user
 * can't find it. Only the yaw (heading about world-up) is cancelled — pitch/roll pass through, so
 * looking up/down still works and panning still moves the world-fixed sign out of frame.
 */
export class YawRecenter {
  private offset: Quat | null = null;

  /** Apply the captured yaw offset; the first call establishes it from that sample's heading. */
  apply(orientation: Quat): Quat {
    if (!this.offset) {
      const forward = applyQuat(orientation, FORWARD);
      const yaw = Math.atan2(forward.x, -forward.z);
      this.offset = quatFromAxisAngle(UP, yaw);
    }
    return quatMultiply(this.offset, orientation);
  }

  /** Drop the reference so the next sample re-centers again (e.g. on restart). */
  reset(): void {
    this.offset = null;
  }
}
